import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import { MusicNote } from '@mui/icons-material';
import { NowPlaying as NowPlayingType } from '../types';
import apiService from '../services/api';
import { figma } from '../theme/figmaTokens';

const cardGradientSx = {
  background: 'linear-gradient(180deg, #1e486e 0%, #132037 100%)',
  border: '1px solid rgba(255,255,255,0.12)',
  boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
};

const selectOnCardSx = {
  color: 'rgba(255,255,255,0.92)',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,255,255,0.25)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,255,255,0.4)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: figma.cyan,
    borderWidth: '1px',
  },
  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.55)' },
};

const labelOnCardSx = { color: 'rgba(255,255,255,0.7)' };

function formatClockTime(d: Date): string {
  let h = d.getHours();
  const m = d.getMinutes();
  const s = d.getSeconds();
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')} ${ampm}`;
}

const nowPlayingHeaderTypographySx = {
  fontWeight: 400,
  fontSize: '0.875rem',
  lineHeight: 1.4,
  color: '#349ACF',
};

const clockTypographySx = {
  fontWeight: 400,
  fontSize: '0.875rem',
  lineHeight: 1.4,
  color: 'rgba(255,255,255,0.7)',
  fontVariantNumeric: 'tabular-nums' as const,
  mt: 0.5,
};

const trackLineSx = {
  fontWeight: 400,
  color: '#ffffff',
  fontSize: '1rem',
  lineHeight: 1.35,
};

interface NowPlayingProps {
  stationName: string;
  callLetters: string;
  selectedCategory: string;
  onCategoryChange: (value: string) => void;
  activeFilter: string;
  onActiveFilterChange: (value: string) => void;
  categories: any[];
}

const NowPlaying: React.FC<NowPlayingProps> = ({
  stationName,
  callLetters,
  selectedCategory,
  onCategoryChange,
  activeFilter,
  onActiveFilterChange,
  categories,
}) => {
  const [nowPlaying, setNowPlaying] = useState<NowPlayingType | null>(null);
  const [loading, setLoading] = useState(true);
  const [clock, setClock] = useState(() => formatClockTime(new Date()));

  useEffect(() => {
    const t = setInterval(() => setClock(formatClockTime(new Date())), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const fetchNowPlaying = async () => {
      try {
        const data = await apiService.getNowPlaying();
        setNowPlaying(data || null);
      } catch (error) {
        console.error('NowPlaying component: Error fetching now playing:', error);
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    fetchNowPlaying();
    const interval = setInterval(fetchNowPlaying, 30000);
    return () => clearInterval(interval);
  }, [stationName]);

  const letters = callLetters.trim();
  const nowPlayingLabel = letters
    ? `Now Playing on ${letters}`
    : `Now Playing on ${stationName || '—'}`;

  if (loading) {
    return (
      <Card sx={{ minWidth: 275, mb: 2, ...cardGradientSx }}>
        <CardContent>
          <Typography variant="h6" component="div" sx={{ color: '#ffffff', fontWeight: 400 }}>
            {stationName}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Loading...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ minWidth: 275, mb: 2, ...cardGradientSx }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            {nowPlaying ? (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                {nowPlaying.artwork ? (
                  <Box
                    component="img"
                    src={nowPlaying.artwork}
                    alt=""
                    sx={{
                      width: 100,
                      height: 100,
                      flexShrink: 0,
                      borderRadius: 1,
                      objectFit: 'cover',
                      border: '1px solid rgba(255,255,255,0.2)',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      flexShrink: 0,
                      borderRadius: 1,
                      backgroundColor: 'rgba(0,0,0,0.25)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <MusicNote sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 40 }} />
                  </Box>
                )}

                <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', pt: 0 }}>
                  <Typography component="div" sx={{ ...nowPlayingHeaderTypographySx, mb: 1 }}>
                    {nowPlayingLabel}
                  </Typography>

                  <Typography component="div" noWrap sx={{ ...trackLineSx, mb: 0.5 }}>
                    {nowPlaying.title}
                  </Typography>
                  <Typography component="div" noWrap sx={{ ...trackLineSx, mb: 0 }}>
                    {nowPlaying.artist}
                  </Typography>
                  <Typography component="div" sx={clockTypographySx}>
                    {clock}
                  </Typography>

                  {nowPlaying.album && (
                    <Chip
                      label={nowPlaying.album}
                      size="small"
                      variant="outlined"
                      sx={{
                        mt: 1,
                        alignSelf: 'flex-start',
                        color: 'rgba(255,255,255,0.9)',
                        borderColor: 'rgba(255,255,255,0.35)',
                        '& .MuiChip-label': { color: 'rgba(255,255,255,0.9)' },
                      }}
                    />
                  )}
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    flexShrink: 0,
                    borderRadius: 1,
                    backgroundColor: 'rgba(0,0,0,0.25)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <MusicNote sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 40 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography component="div" sx={{ ...nowPlayingHeaderTypographySx, mb: 1 }}>
                    {nowPlayingLabel}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mb: 0.5 }}>
                    No track information available
                  </Typography>
                  <Typography component="div" sx={clockTypographySx}>
                    {clock}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 280 }}>
            <FormControl size="small" fullWidth>
              <InputLabel sx={labelOnCardSx}>Category</InputLabel>
              <Select
                value={selectedCategory}
                onChange={(e) => onCategoryChange(e.target.value)}
                label="Category"
                sx={selectOnCardSx}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel sx={labelOnCardSx}>Status</InputLabel>
              <Select
                value={activeFilter}
                onChange={(e) => onActiveFilterChange(e.target.value)}
                label="Status"
                sx={selectOnCardSx}
              >
                <MenuItem value="all">All Messages</MenuItem>
                <MenuItem value="active">On Air - Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default NowPlaying;
